import React from 'react';
import { Link, Text } from 'office-ui-fabric-react';
import {
    TextField,
    IconButton,
    IIconProps,
    DefaultButton,
    PrimaryButton,
    TooltipHost,
    IImageProps,
    Image,
    Checkbox,
    Icon
} from '@fluentui/react';
import InfoIcon from '../../../static/icons/tooltip-info-icon.svg';
import { IAddCustomerData, ISelectedData } from 'app/models/common/response';
import NumberFormat from 'react-number-format';
import { keyPress } from 'app/components/helpers';
import { RequestDomain, StringConstants, TelemetryConstants } from 'app/utils/constants';
import { useId } from '@fluentui/react-hooks';
import { CountryMaster, RequestType } from 'generated/request-backend';
import ImpactReasonFields, { isImpactReasonComplete } from '../ImpactReasonFields';
import { useIsNewTicketViewEnabled } from 'app/modules/newTicketView/viewFlag';
import { logFlowEvent } from 'app/modules/user/createTicket/flowTelemetry';
import { SAME_ISSUE_DESCRIPTION } from 'app/modules/user/createTicket/customerImpact/impactDescriptionConstants';

const IMPACT_DESCRIPTION_MAX = 2000;
const IMPACT_DESCRIPTION_COUNTER_THRESHOLD = 1600;

interface IAddDetailsProps {
    isEdit?: boolean;
    retrySynchronization?: boolean;
    invalidEditCustomerName?: boolean;
    selectedRequest: IAddCustomerData;
    setDisplayScreen?: (displayScreen: string) => void;
    onDismiss?: () => void;
    selectedData: Array<ISelectedData>;
    handleChange: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, type: string) => void;
    handleChanges: (changes: Partial<ISelectedData>) => void;
    handleAddCustomer?: () => void;
    customerHelperText?: boolean;
    newCountryHelperText?: boolean;
    errorCustomerName?: boolean;
    errorCountry?: boolean;
    errorEstimatedValue?: boolean;
    errorTotalDeviceCount?: boolean;
    multiFormError?: boolean;

    type?: string;
    cssNumber?: string;
    setCssNumber?: (cssNumber: string) => void;
    setErrorText?: (errorText: boolean) => void;
    cssErrorText?: boolean;
    countryList: Array<CountryMaster>;
}

const AddNewDetails: React.FC<IAddDetailsProps> = (props) => {
    const {
        selectedRequest,
        onDismiss,
        setDisplayScreen,
        selectedData,
        handleChange,
        handleChanges,
        handleAddCustomer,
        errorCustomerName,
        errorCountry,
        errorEstimatedValue,
        errorTotalDeviceCount,
        multiFormError,

        type,
        cssNumber,
        setCssNumber,
        cssErrorText,
        setErrorText,
        countryList,
        isEdit = false,
        retrySynchronization = false,
        invalidEditCustomerName = false
    } = props;

    const cancelIcon: IIconProps = { iconName: 'Cancel' };
    const calloutProps = { gapSpace: 0 };
    const tooltipId = useId('tooltip');
    const isNewTicketViewEnabled = useIsNewTicketViewEnabled();

    const cancelHandler = () => {
        if (isEdit) { onDismiss(); return; }
        if (isNewTicketViewEnabled) {
            logFlowEvent(
                type === StringConstants.ADD_NEW_DETAILS
                    ? TelemetryConstants.CUSTOMER_MANUAL_ENTRY_CANCELLED
                    : TelemetryConstants.CUSTOMER_IMPACT_PANEL_CANCELLED,
                'Add Customer'
            );
        }
        setDisplayScreen('searchCust');
    };

    const imageProps: IImageProps = {
        styles: () => ({ root: { paddingBottom: '10px' } })
    };

    const addHandler = () => {
        if (selectedRequest.requestTeam === RequestDomain.CSS) {
            if (cssNumber === '' || cssNumber === null) {
                setErrorText(true);
            } else {
                logFlowEvent(TelemetryConstants.CUSTOMER_IMPACT_PANEL_COMPLETED, 'Add Customer');
                handleAddCustomer();
            }
        } else {
            logFlowEvent(
                type === StringConstants.ADD_NEW_DETAILS
                    ? TelemetryConstants.CUSTOMER_MANUAL_ENTRY_COMPLETED
                    : TelemetryConstants.CUSTOMER_IMPACT_PANEL_COMPLETED,
                'Add Customer'
            );
            handleAddCustomer();
        }
    };

    const fireChange = (fieldType: string, value: string) =>
        handleChange(
            { target: { value } } as unknown as React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            fieldType
        );

    const handleSameIssue = (checked: boolean) => {
        handleChanges({
            sameIssue: checked,
            customerImpact: checked ? (isEdit ? SAME_ISSUE_DESCRIPTION : (selectedRequest.summary || '')) : ''
        });
    };

    if (isNewTicketViewEnabled) {
        const item = selectedData[0] || ({} as ISelectedData);
        const isManualCustomer = type === StringConstants.ADD_NEW_DETAILS;
        const descriptionLength = item.customerImpact?.length ?? 0;
        const headingText = isEdit ? 'Edit customer impact' : isManualCustomer ? 'Add a new customer' : 'Add customer';
        const cssCaseBlock =
            (selectedRequest.requestType === RequestType.CssReviewRequest ||
                (isEdit && selectedRequest.requestType === RequestType.CSSRequest) ||
                selectedRequest.requestType === RequestType.CXP_TechnicalRequest) && (
                <div className="info-css-textfield-container row-flex-box m-b-25">
                    <div className="css-case-styles">
                        <div className="tooltip-styles">
                            <label className="label-styles">CSS Case Number</label>
                            <Text>
                                <TooltipHost
                                    className="tooltip-display"
                                    id={tooltipId}
                                    calloutProps={calloutProps}
                                    content="For this type of request please have a CSS support case open first">
                                    <Icon
                                        iconName="Info"
                                        className="field-info-icon"
                                        aria-describedby={tooltipId}
                                    />
                                </TooltipHost>
                            </Text>
                        </div>
                        <div className="info-container">
                            <TextField
                                value={cssNumber}
                                ariaLabel="CSS Case Number"
                                data-testid="css-case-number-input"
                                onChange={(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                    setCssNumber((e.target as HTMLInputElement | HTMLTextAreaElement).value);
                                    setErrorText(false);
                                }}
                            />
                        </div>
                        {cssErrorText && (
                            <div className="error-info-message">
                                <span className="helper-text">Please enter the CSS Case Number.</span>
                            </div>
                        )}
                    </div>
                </div>
            );

        return (
            <div className="selected-customer-container">
                <div className="close-model">
                    <IconButton
                        role="button"
                        aria-label="close popup"
                        iconProps={cancelIcon}
                        ariaLabel="Close popup modal"
                        data-testid="add-customer-close-button"
                        tabIndex={0}
                        onClick={() => onDismiss()}
                        onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) => keyPress(e, onDismiss)}
                    />
                </div>
                <div className="modal-heading">
                    <span>{headingText}</span>
                    {(selectedRequest.unifiedTrackingId || selectedRequest.summary) && (
                        <div className="drawer-subtitle">
                            {selectedRequest.unifiedTrackingId}
                            {selectedRequest.unifiedTrackingId && selectedRequest.summary ? ' · ' : ''}
                            {selectedRequest.summary}
                        </div>
                    )}
                </div>
                <div className="modal-body">
                    <div className="body-result-container">
                        {cssCaseBlock}
                        <div className="opportunity-impact-form">
                            <div className="opportunity-impact-form-body">
                                {isManualCustomer ? (
                                <div className="impacted-text-fields">
                                    <div>
                                        <label className="label-styles">
                                            Customer Name<span className="required-star">*</span>
                                            <TooltipHost
                                                calloutProps={calloutProps}
                                                content="Enter the customer's company name">
                                                <Icon iconName="Info" className="field-info-icon" />
                                            </TooltipHost>
                                        </label>
                                        <TextField
                                            value={item.accountName}
                                            ariaLabel="Customer Name"
                                            aria-required={true}
                                            aria-invalid={!!errorCustomerName}
                                            aria-describedby={errorCustomerName ? 'customer-name-error' : undefined}
                                            required
                                            placeholder="e.g., Contoso Ltd."
                                            onChange={(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                                if (isEdit) handleChanges({ accountName: newValue ?? '' });
                                                else handleChange(e, 'accountName');
                                            }}
                                            data-testid="accountName-text"
                                        />
                                        {errorCustomerName && (
                                            <span className="helper-text" id="customer-name-error">
                                                Please enter a customer name.
                                            </span>
                                        )}
                                    </div>
                                    <div className="country-select">
                                        <label className="label-styles">
                                            Country<span className="required-star">*</span>
                                            <TooltipHost
                                                calloutProps={calloutProps}
                                                content="Select the customer's country or subsidiary">
                                                <Icon iconName="Info" className="field-info-icon" />
                                            </TooltipHost>
                                        </label>
                                        <select
                                            value={item.country ?? ''}
                                            onChange={(e: React.FormEvent<HTMLSelectElement>) =>
                                                handleChange(e, 'country')
                                            }
                                            required
                                            aria-required="true"
                                            aria-invalid={!!errorCountry}
                                            aria-describedby={errorCountry ? 'customer-country-error' : undefined}
                                            aria-label="Enter customer country"
                                            data-testid="country-text"
                                            className={`format-field-cont ${item.country ? '' : 'placeholder-value'}`}>
                                            <option value="" disabled>
                                                e.g., United States
                                            </option>
                                            {countryList?.map((country: CountryMaster, key) => (
                                                <option key={key} value={country.name}>
                                                    {country.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errorCountry && (
                                            <span className="helper-text" id="customer-country-error">
                                                Please enter the country to which the customer belongs.
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Text className="impact-section-heading">Customer details</Text>
                                    <div className="opportunity-details-section">
                                        <div className="details-grid">
                                            <div className="detail-row">
                                                <Text className="detail-label">Customer name</Text>
                                                <Text className="detail-value">{item.accountName}</Text>
                                            </div>
                                            <div className="detail-row">
                                                <Text className="detail-label">Account ID</Text>
                                                <Text className="detail-value">{item.accountNumber}</Text>
                                            </div>
                                            <div className="detail-row">
                                                <Text className="detail-label">Country</Text>
                                                <Text className="detail-value">{item.country}</Text>
                                            </div>
                                        </div>
                                    </div>
                                    <Text className="impact-section-heading">Estimate the impact of this issue</Text>
                                </>
                            )}
                            <div className="impacted-text-fields">
                                <div>
                                    <label className="label-styles">
                                        Revenue at risk<span className="required-star">*</span>
                                        <TooltipHost calloutProps={calloutProps} content="Estimated revenue at risk in USD">
                                            <Icon iconName="Info" className="field-info-icon" />
                                        </TooltipHost>
                                    </label>
                                    <NumberFormat
                                        aria-label="Revenue at risk"
                                        aria-required="true"
                                        aria-invalid={!!errorEstimatedValue}
                                        aria-describedby={errorEstimatedValue ? 'revenue-at-risk-error' : undefined}
                                        required
                                        data-testid="revenue-at-risk-input"
                                        thousandSeparator={true}
                                        prefix="$"
                                        value={item.estimatedValue}
                                        onChange={(e: React.FormEvent<HTMLInputElement>) =>
                                            handleChange(e, 'impacted_revenue')
                                        }
                                        placeholder="e.g., $500,000"
                                        allowNegative={true}
                                        inputMode="numeric"
                                        className="num-foramt"
                                    />
                                    {errorEstimatedValue && (
                                        <span className="helper-text" id="revenue-at-risk-error">
                                            Please enter the revenue risk involved.
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <label className="label-styles">
                                        Devices affected<span className="required-star">*</span>
                                        <TooltipHost calloutProps={calloutProps} content="Number of devices impacted">
                                            <Icon iconName="Info" className="field-info-icon" />
                                        </TooltipHost>
                                    </label>
                                    <NumberFormat
                                        aria-label="Devices affected"
                                        aria-required="true"
                                        aria-invalid={!!errorTotalDeviceCount}
                                        aria-describedby={errorTotalDeviceCount ? 'devices-affected-error' : undefined}
                                        required
                                        data-testid="devices-affected-input"
                                        thousandSeparator={true}
                                        value={item.totalDeviceCount}
                                        onChange={(e: React.FormEvent<HTMLInputElement>) =>
                                            handleChange(e, 'impacted_device')
                                        }
                                        placeholder="e.g., 200"
                                        allowNegative={true}
                                        inputMode="numeric"
                                        className="num-foramt"
                                        decimalScale={0}
                                    />
                                    {errorTotalDeviceCount && (
                                        <span className="helper-text" id="devices-affected-error">
                                            Please enter the device risk involved.
                                        </span>
                                    )}
                                </div>
                            </div>
                            <ImpactReasonFields
                                item={item}
                                showSameIssue={false}
                                onFieldChange={(fieldType, value) => fireChange(fieldType, value)}
                            />
                            <div className="impact-description-field">
                                <label className="label-styles">
                                    Customer impact description<span className="required-star">*</span>
                                </label>
                                <div className="impact-checkbox">
                                    <Checkbox
                                        label="This customer also has the same issue as the ticket"
                                        checked={!!item.sameIssue}
                                        ariaLabel="This customer also has the same issue as the ticket"
                                        data-testid="impact-same-issue-checkbox"
                                        onChange={(_e, checked) => handleSameIssue(!!checked)}
                                    />
                                </div>
                                <TextField
                                    className="cust-impact"
                                    multiline={true}
                                    maxLength={IMPACT_DESCRIPTION_MAX}
                                    ariaLabel="Describe how this issue is impacting this customer"
                                    aria-required={true}
                                    aria-invalid={!item.customerImpact?.trim()}
                                    required
                                    data-testid="customer-impact-description"
                                    placeholder="Describe how this issue is impacting this customer specifically..."
                                    onChange={(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                        if (isEdit) {
                                            const value = newValue ?? '';
                                            handleChanges({ customerImpact: value, sameIssue: value === SAME_ISSUE_DESCRIPTION });
                                        } else {
                                            handleChange(e, 'customer_impact');
                                        }
                                    }}
                                    value={item.customerImpact}
                                />
                                {descriptionLength >= IMPACT_DESCRIPTION_COUNTER_THRESHOLD && (
                                    <div className="char-counter">
                                        {descriptionLength}/{IMPACT_DESCRIPTION_MAX}
                                    </div>
                                )}
                            </div>
                            </div>
                            {multiFormError && (
                                <div className="helper-txt" role="alert" aria-live="assertive">
                                    Please provide customer impact details for this customer.
                                </div>
                            )}
                            <div className="add-details-btn">
                                <DefaultButton
                                    tabIndex={0}
                                    onClick={cancelHandler}
                                    onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                                        keyPress(e, cancelHandler)
                                    }
                                    text={isEdit ? 'Cancel' : 'Back'}
                                    data-testid="add-customer-back-button"
                                />
                                <PrimaryButton
                                    tabIndex={0}
                                    onClick={addHandler}
                                    onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                                        keyPress(e, addHandler)
                                    }
                                    text={isEdit ? (retrySynchronization ? 'Retry synchronization' : 'Save changes') : 'Add to ticket'}
                                    data-testid="add-customer-submit-button"
                                    disabled={
                                        (isEdit && invalidEditCustomerName) || !isImpactReasonComplete(item) ||
                                        !item.customerImpact?.trim()
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="selected-customer-container">
            <div className={`close-model `}>
                <IconButton
                    role="button"
                    aria-label="close popup"
                    iconProps={cancelIcon}
                    ariaLabel="Close popup modal"
                    tabIndex={0}
                    onClick={() => onDismiss()}
                    onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) => keyPress(e, onDismiss)}
                />
            </div>
            <div className="modal-heading">
                <span>Add a customer</span>
            </div>
            <div className="modal-body">
                <div className="body-main-container">
                    <Text className="modal-body-title back-title">
                        <Link
                            aria-label="back button"
                            tabIndex={0}
                            onClick={cancelHandler}
                            onKeyPress={(e: React.KeyboardEvent<HTMLAnchorElement>) => keyPress(e, cancelHandler)}
                            data-testid="search-customer">
                            <span className="arrow-icon"></span>
                        </Link>
                        Add Details
                    </Text>
                    <div className="body-main-sub">
                        <div>
                            <Text className="secondary-text mb-10">
                                <span className="text-bold">Request ID - </span>
                                {selectedRequest.unifiedTrackingId}
                            </Text>
                            <Text className="secondary-text mb-10">
                                <span className="text-bold">Application ID - </span>
                                {selectedRequest.applicationId}
                            </Text>
                        </div>
                        <div>
                            <Text className="modal-body-title title-field">{selectedRequest.summary}</Text>
                        </div>
                        {type === StringConstants.ADD_DETAILS && (
                            <div className="cust-details">
                                <Text className="secondary-text">
                                    <span className="text-bold">Customer Name: </span>
                                    {selectedData[0].accountName}
                                </Text>
                                <Text className="secondary-text">
                                    <span className="text-bold">Account ID: </span>
                                    {selectedData[0].accountNumber}
                                </Text>

                                <Text className="secondary-text">
                                    <span className="text-bold">Country/Subsidiary: </span>
                                    {selectedData[0].country}
                                </Text>
                            </div>
                        )}
                    </div>
                </div>
                <div className="body-result-container">
                {(selectedRequest.requestType === RequestType.CssReviewRequest ||
                        (isEdit && selectedRequest.requestType === RequestType.CSSRequest) ||
                        selectedRequest.requestType === RequestType.CXP_TechnicalRequest) && (
                            <div className="info-css-textfield-container row-flex-box m-b-25">
                            <div className="css-case-styles">
                                <div className="tooltip-styles">
                                    <label className="label-styles">CSS Case Number</label>
                                    <Text>
                                        <TooltipHost
                                            className="tooltip-display"
                                            id={tooltipId}
                                            calloutProps={calloutProps}
                                            content={
                                                'For this type of request please have a CSS support case open first'
                                            }>
                                            <Image
                                                {...imageProps}
                                                aria-describedby={tooltipId} src={InfoIcon} alt="Info"
                                            />
                                        </TooltipHost>
                                    </Text>
                                </div>
                                <div className="info-container">
                                <TextField
                                    ariaLabel={isEdit ? 'CSS Case Number' : undefined}
                                    required={isEdit}
                                    value={cssNumber}
                                    onChange={(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                        setCssNumber(isEdit ? (newValue ?? '') : (e.target as HTMLInputElement | HTMLTextAreaElement).value);
                                        setErrorText(false);
                                    }}
                                />
                                </div>
                                <div>
                                    {cssErrorText &&
                                        <div className="error-info-message">
                                            <span className="helper-text">Please enter the CSS Case Number.</span>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    )}
                    {type === StringConstants.ADD_NEW_DETAILS && (
                        <div className="impacted-text-fields">
                            <div className="impacted-text-fields">
                                <div>
                                    <label>
                                        Customer Name<span className="required">*</span>
                                    </label>
                                    <TextField
                                        value={selectedData[0]?.accountName}
                                        onChange={(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                            if (isEdit) handleChanges({ accountName: newValue ?? '' });
                                            else handleChange(e, 'accountName');
                                        }}
                                        data-testid="accountName-text"
                                    />
                                    {errorCustomerName && <span className="helper-text">Please enter a customer name.</span>}
                                </div>
                                <div className="country-select">
                                    <label>
                                        Country/Subsidiary<span className="required">*</span>
                                    </label>
                                    <select
                                        value={selectedData[0]?.country}
                                        onChange={(e: React.FormEvent<HTMLSelectElement>) => handleChange(e, 'country')}
                                        aria-label="Enter customer country"
                                        data-testid="country-text"
                                        className="format-field-cont">
                                        {countryList?.map((item: CountryMaster, key) => {
                                            return (
                                                <option key={key} value={item.name}>
                                                    {item.name}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    {errorCountry && (
                                        <span className="helper-text">
                                            Please enter the country to which the customer belongs.
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="impacted-text-fields">
                        <div>
                            <label>Revenue Risk ($)</label>
                            <NumberFormat
                                aria-label="Revenue risk"
                                data-testid="revenue-risk-input"
                                thousandSeparator={true}
                                value={selectedData[0]?.estimatedValue}
                                onChange={(e: React.FormEvent<HTMLInputElement>) => handleChange(e, 'impacted_revenue')}
                                placeholder="$"
                                allowNegative={true}
                                inputMode="numeric"
                                className="num-foramt"
                            />
                            {errorEstimatedValue && <span className="helper-text">Please enter the revenue risk involved.</span>}
                        </div>

                        <div>
                            <label>Device Risk</label>
                            <NumberFormat
                                aria-label="Device risk"
                                data-testid="device-risk-input"
                                thousandSeparator={true}
                                value={selectedData[0]?.totalDeviceCount}
                                onChange={(e: React.FormEvent<HTMLInputElement>) => handleChange(e, 'impacted_device')}
                                placeholder="0"
                                allowNegative={true}
                                inputMode="numeric"
                                className="num-foramt"
                                decimalScale={0}
                            />
                            {errorTotalDeviceCount && <span className="helper-text">Please enter the device risk involved.</span>}
                        </div>
                    </div>

                    {isNewTicketViewEnabled && (
                        <ImpactReasonFields
                            item={selectedData[0] || ({} as ISelectedData)}
                            onFieldChange={(fieldType, value) =>
                                handleChange(
                                    { target: { value } } as unknown as React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                                    fieldType
                                )
                            }
                        />
                    )}

                    {isEdit && (
                        <div>
                            <label className="label-styles">Please tell us how this impacts your customer</label>
                            <TextField
                                ariaLabel="Enter How this impacts your customer"
                                className="cust-impact"
                                multiline
                                maxLength={IMPACT_DESCRIPTION_MAX}
                                value={selectedData[0]?.customerImpact}
                                onChange={(_event, newValue) => {
                                    const value = newValue ?? '';
                                    handleChanges({ customerImpact: value, sameIssue: value === SAME_ISSUE_DESCRIPTION });
                                }}
                            />
                        </div>
                    )}
                    {multiFormError && (
                        <div className="helper-txt" role="alert" aria-live="assertive">
                            Please provide customer impact details for this customer.
                        </div>
                    )}
                    <div className="add-details-btn">
                        <DefaultButton
                            tabIndex={0}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                onDismiss();
                            }}
                            onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) => keyPress(e, cancelHandler)}
                            text="Cancel"
                        />
                        <PrimaryButton
                            tabIndex={0}
                            onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) => addHandler()}
                            onClick={() => addHandler()}
                            text={isEdit ? 'Save changes' : 'Next'}
                            disabled={isEdit && invalidEditCustomerName}
                            data-testid="add-customer-next-button"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddNewDetails;
